import { useMemo, useState } from 'react'
import type { DirectoryFilterGroup } from '@kkf/design'

/**
 * The state behind a filterable listing: one query string, one set of selections per
 * group, and the filtered result.
 *
 * ── Why this is a hook in the app and not a component in the design system ────────────
 *
 * The design system owns the *shapes* — `AdvListing` is the two-column shell,
 * `DirectoryFilters` the rail, `AdvEmpty` the nothing-matched state. This is the
 * behaviour, and it is behaviour three pages of ONE app share: tours, services and
 * programmes. Ground rule 5 promotes a shared block to `app_design` the moment a second
 * app needs it, and no second app does yet — Partners' directory has its own
 * filtering with different rules (it filters on two independent axes and this filters on
 * one). Promoting it now would mean generalising over one real case and one imagined one.
 *
 * ── The counts are computed against the query, not against everything ─────────────────
 *
 * A checkbox that says "Workshop 2" while the search has narrowed the page to one result
 * is lying about what clicking it will do. So the counts come from the query-filtered set
 * rather than from the whole list — which is also why they can read zero, and a zero count
 * is useful information rather than a bug.
 *
 * Selecting nothing in a group means "no constraint from this group", not "match nothing".
 * That is what a visitor expects from a set of checkboxes, and it is why the page opens
 * showing everything.
 */
export interface ListingFilterConfig<T> {
  items: T[]
  /** The group's stable key, e.g. `kind`. */
  groupId: string
  /** The rail's heading for that group, already translated. */
  groupTitle: string
  /**
   * What to SHOW for a group's value. The value itself is a discriminant and is never
   * translated — see `data/vocabulary.ts`. Omitted, the value is its own label.
   */
  labelOf?: (value: string) => string
  /** The value this item belongs to — the checkbox it is counted under. */
  groupOf: (item: T) => string
  /** Every word of this item the search should match. Joined and lower-cased once. */
  searchText: (item: T) => string
  /** The order the checkboxes appear in. Values absent from `items` are dropped. */
  order?: string[]
}

export interface ListingFilterResult<T> {
  search: string
  setSearch: (value: string) => void
  selected: Record<string, string[]>
  toggle: (groupId: string, value: string) => void
  clear: () => void
  /** True when anything is narrowing the list — drives the empty state's offer. */
  isFiltered: boolean
  results: T[]
  groups: DirectoryFilterGroup[]
  /** "4 of 6 shown" — what the rail prints above the groups. */
  summary: string
}

export function useListingFilter<T>({
  items,
  groupId,
  groupTitle,
  groupOf,
  labelOf,
  searchText,
  order,
}: ListingFilterConfig<T>): ListingFilterResult<T> {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Record<string, string[]>>({})

  const query = search.trim().toLowerCase()

  // The query pass, on its own, because the checkbox counts are computed against it.
  const byQuery = useMemo(
    () => (query === '' ? items : items.filter((item) => searchText(item).toLowerCase().includes(query))),
    // `searchText` is a fresh closure on every render, so including it would recompute
    // every time. The data is a module constant and the accessor is pure, so `items` and
    // `query` are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, query],
  )

  const chosen = selected[groupId] ?? []

  const results = useMemo(
    () => (chosen.length === 0 ? byQuery : byQuery.filter((item) => chosen.includes(groupOf(item)))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [byQuery, chosen],
  )

  const groups: DirectoryFilterGroup[] = useMemo(() => {
    const counts = new Map<string, number>()
    for (const item of byQuery) {
      const value = groupOf(item)
      counts.set(value, (counts.get(value) ?? 0) + 1)
    }
    // Every value that exists in the FULL list gets an option, so a checkbox does not
    // vanish as you type — a control that disappears while you are reaching for it is
    // worse than one showing zero.
    const all = new Set(items.map(groupOf))
    const ordered = order ? order.filter((v) => all.has(v)) : [...all].sort()
    return [
      {
        id: groupId,
        title: groupTitle,
        options: ordered.map((value) => ({
          value,
          label: labelOf ? labelOf(value) : value,
          count: counts.get(value) ?? 0,
        })),
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byQuery, items, groupId, groupTitle, order, labelOf])

  const toggle = (id: string, value: string) =>
    setSelected((cur) => {
      const values = cur[id] ?? []
      return { ...cur, [id]: values.includes(value) ? values.filter((v) => v !== value) : [...values, value] }
    })

  const clear = () => {
    setSearch('')
    setSelected({})
  }

  const isFiltered = query !== '' || chosen.length > 0

  return {
    search,
    setSearch,
    selected,
    toggle,
    clear,
    isFiltered,
    results,
    groups,
    summary: `${results.length} of ${items.length} shown`,
  }
}
